import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { GridSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { Grid } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { IChildren, gridTags } from 'app/models/Placement/PlacementMods';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectEditPreferenceChildrenRows,
  selectExpandedModsDialog,
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsUndoModal,
  selectPreferenceChildrenAgeError,
  selectPreferenceChildrenGenderError,
} from 'store/redux-store/placement-mods/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { makeStyles } from 'tss-react/mui';
import { generateRandomId, getChildrenGridColumns } from './utils';

const expandedDetailPanelStyles = makeStyles()({
  expandedGrid: {
    fontSize: '14px',
    '& .MuiDataGrid-main': {
      minHeight: '60px',
    },
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  isErrorCell: {
    border: '1px solid !important',
    borderColor: 'red',
    color: 'red',
  },
});

export const ChildrenGrid = ({ childrenTableInfo }: { childrenTableInfo: IChildren[] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //*START - SELECTORS
  const isExpanded = useSelector(selectExpandedModsDialog);
  const isModEdit = useSelector(selectIsEditMod);
  const isUndoModal = useSelector(selectIsUndoModal);
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);

  //Grid Specific selectors
  const editPreferenceChildrenRequests = useSelector(selectEditPreferenceChildrenRows);
  const preferenceChildrenGenderError = useSelector(selectPreferenceChildrenGenderError);
  const preferenceChildrenAgeError = useSelector(selectPreferenceChildrenAgeError);
  //*END - SELECTORS

  const { classes } = expandedDetailPanelStyles();
  const [headerCount, setHeaderCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [columns, setColumns] = useState<any>(
    getChildrenGridColumns(t, isExpanded, isModEdit, classes, editPreferenceChildrenRequests),
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'gender', sort: 'asc' }]);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);

  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'preferences-details-api', delay: 0 });

  //SET COLUMNS
  useEffect(() => {
    const initialColumns = getChildrenGridColumns(t, isExpanded, isModEdit, classes, editPreferenceChildrenRequests);
    setColumns(initialColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isExpanded, isModEdit, editPreferenceChildrenRequests]);

  //HANDLES THE ROW SELECTION,ENABLE/DISABLE DELETE BUTTON
  const onRowsSelectionHandler = ids => {
    setSelectedIds(ids);
    const modData = isModEdit ? editPreferenceChildrenRequests : childrenTableInfo;
    const selectedRowsData = ids.map(id => modData.find(row => row.id === id));
    selectedRowsData.length > 0 ? setIsDeleteDisabled(false) : setIsDeleteDisabled(true);
  };
  //DELETE SELECTED ROWS,UPDATES EDIT DATA STATE , ERRORS , MOD ALTERED STATUS
  const deleteHandler = () => {
    const modData = isModEdit ? editPreferenceChildrenRequests : childrenTableInfo;
    const updatedDataAfterDelete = modData.filter(item => !selectedIds.includes(item.id));
    const updateGenderError = preferenceChildrenGenderError.filter(item => !selectedIds.includes(item.id));
    const updateAgeError = preferenceChildrenAgeError.filter(item => !selectedIds.includes(item.id));

    dispatch(placementModsAction.setChildrenGridModAltered(true));
    dispatch(placementModsAction.setPreferenceChildrenGenderError(updateGenderError));
    dispatch(placementModsAction.setPreferenceChildrenAgeError(updateAgeError));
    dispatch(placementModsAction.setEditChildrenRows(updatedDataAfterDelete));

    setSelectedIds([]);
  };

  //ADD A NEW ROW,UPDATES EDIT DATA STATE , ERRORS , MOD ALTERED STATUS
  const addNewChildrenRow = e => {
    const existingRow = editPreferenceChildrenRequests?.slice(0);
    const childrenGenderError = preferenceChildrenGenderError?.slice(0);
    const childrenAgeError = preferenceChildrenAgeError?.slice(0);

    const newRow = {
      id: generateRandomId(),
      gender: '',
      age: '',
    };
    existingRow.unshift(newRow);

    const genderError = {
      gender: true,
      id: newRow?.id,
    };
    const ageError = {
      age: true,
      id: newRow?.id,
    };

    childrenGenderError.unshift(genderError);
    childrenAgeError.unshift(ageError);
    dispatch(placementModsAction.setPreferenceChildrenGenderError(childrenGenderError));
    dispatch(placementModsAction.setPreferenceChildrenAgeError(childrenAgeError));
    dispatch(placementModsAction.setChildrenGridModAltered(true));
    dispatch(placementModsAction.setEditChildrenRows(existingRow));
  };

  //REFRESH GRID DATA
  const handleRefreshGrid = () => {
    setSortModel([{ field: 'gender', sort: 'asc' }]);
    dispatch(
      placementModsAction.getPreferencesModDetailAction({
        placementId: selectedModsDetails.placementId,
        travelerOptionId: selectedModsDetails.travelerOptionId,
        newMod: !isEditExistingMod,
      }),
    );
  };

  //RESET COLUMN
  const resetColumns = () => {
    setColumns(getChildrenGridColumns(t, isExpanded, isModEdit, classes, editPreferenceChildrenRequests));
  };

  //ADD/DELETE ACTION
  const rightActions = React.useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-new-childrenInfo',
        Icon: AddIcon,
        disabled: !isModEdit,
        onClick: e => {
          addNewChildrenRow(e);
        },
        tooltipProps: {
          tooltipContent: 'Add Children',
        },
      },
      {
        key: 'delete',
        Icon: DeleteOutlinedIcon,
        disabled: !selectedIds || selectedIds?.length === 0,
        onClick: () => deleteHandler(),
        tooltipProps: {
          tooltipContent: 'Delete',
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isExpanded,
    isModEdit,
    isDeleteDisabled,
    selectedIds,
    editPreferenceChildrenRequests,
    preferenceChildrenGenderError,
    preferenceChildrenAgeError,
  ]);

  //GET HEADER COUNT METHOD
  const getHeaderCount = rows => {
    const totalRowCount = rows?.length;
    const nullRowCount = rows?.filter(
      each => each?.gender === '' || each?.gender === null || each?.age === null || each?.age === '',
    )?.length;
    setHeaderCount(totalRowCount - nullRowCount);
  };
  //SET GRID ERROR FOR NP DATA,UPDATE HEADER COUNT
  useEffect(() => {
    if (isModEdit) {
      setSortModel([]);
    }
    getHeaderCount(isModEdit ? editPreferenceChildrenRequests : childrenTableInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPreferenceChildrenRequests]);

  //UNDO ACTION
  useEffect(() => {
    if (isUndoModal) {
      dispatch(placementModsAction.setPreferenceChildrenGenderError([]));
      dispatch(placementModsAction.setPreferenceChildrenAgeError([]));
      dispatch(placementModsAction.setChildrenGridModError(false));
      dispatch(placementModsAction.setIsUndoModal(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUndoModal]);

  //SET  CELL ERRORS
  useEffect(() => {
    if (preferenceChildrenGenderError?.some(val => val.gender) || preferenceChildrenAgeError.some(val => val.age)) {
      dispatch(placementModsAction.setChildrenGridModError(true));
    } else {
      dispatch(placementModsAction.setChildrenGridModError(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceChildrenGenderError, preferenceChildrenAgeError, editPreferenceChildrenRequests]);

  const sortChangeHandler = (model: GridSortModel) => {
    setSortModel(model);
  };
  const ChildrenInfoGrid = React.useMemo(() => {
    return (
      <Grid>
        <XGrid
          components={{
            NoRowsOverlay: () => {
              return (
                <div className="center" style={{ height: '100%' }}>
                  {t('placement.placementMods.noRecordsFound')}
                </div>
              );
            },
          }}
          gridTag={gridTags.CHILDRENGRIDTAG}
          rows={isModEdit ? editPreferenceChildrenRequests || [] : childrenTableInfo || []}
          columns={columns}
          disableSelectionOnClick
          pagination={false}
          autoHeight={true}
          sx={{ minWidth: '500px' }}
          sortModel={sortModel}
          onSortModelChange={sortChangeHandler}
          hideFooter
          editMode="row"
          inDialog
          title={t('placement.placementMods.children')}
          displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
          loading={isCallingAPI}
          className={classes.expandedGrid}
          checkboxSelection={isModEdit}
          selectionModel={selectedIds}
          onSelectionModelChange={ids => onRowsSelectionHandler(ids)}
          toolbarProps={{
            refreshButton: true,
            rightActions: rightActions,
            columnSelector: isModEdit ? false : true,
            columnSelectorProps: {
              fallbackPlacements: [
                'bottom-end',
                'bottom',
                'bottom-start',
                'top-start',
                'top',
                'top-end',
                'left',
                'right',
              ],
            },
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenTableInfo, isExpanded, isCallingAPI, resetColumns, columns, sortModel, editPreferenceChildrenRequests]);

  return <>{ChildrenInfoGrid}</>;
};
